import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { notFound, useRouter } from "@tanstack/react-router";

import { apiUrl } from "@workspace/base-api/apiUrl";
import {
	HttpRequestError,
	isHttpNotFound,
	isHttpStatus,
} from "@workspace/base-api/fetchJson";

import { getProfileOptions } from "./profile-api";

export type ProfileContext = ReturnType<typeof useProfileContext>;

export const ProfileContext = React.createContext<ProfileContext | null>(null);

// The stored filename is stable per user (`pfp/{uid}/profile`), so we bust the
// browser cache with a version derived from a caller-supplied token (typically
// the profile's `photoVersion` / updated timestamp). Without a version, the URL
// is still usable but will be served from cache until explicitly invalidated.
export function getPfpUrl(uid: string, version?: string | number | null) {
	const base = `${apiUrl}files/pfp/${uid}/profile`;
	return version ? `${base}?v=${version}` : base;
}

export const getProfileContext = useProfileContext;

export function useProfileContext({
	tenant,
	userId,
	router: r,
	defaultSidebarOpen,
}: {
	tenant: string | null | undefined;
	userId: string | null | undefined;
	router: ReturnType<typeof useRouter> | null;
	defaultSidebarOpen: boolean;
}) {
	const tr = useRouter();
	const router = r || tr;
	const query = useQuery({
		...getProfileOptions(tenant || "", userId || ""),
		enabled: !!tenant && !!userId,
		staleTime: 10 * 60 * 1000, // 10 minutes
	});

	// Keep router-level loaders in sync with the profile query. Triggered only
	// on identity (tenant/userId) changes — TanStack Query handles refetching
	// on the key itself, so we don't need to refetch here.
	React.useEffect(() => {
		if (!tenant || !userId) return;
		router.invalidate();
	}, [tenant, userId, router]);

	React.useEffect(() => {
		if (query.isError && query.error instanceof HttpRequestError) {
			const error = query.error;
			if (isHttpNotFound(error)) {
				throw notFound();
			} else if (isHttpStatus(error, 401)) {
				router.navigate({
					to: "/login",
				});
			} else {
				console.error("Error fetching profile:", error);
			}
		}
	}, [query.isError, query.error, router]);

	const dateFormatOptions = Intl.DateTimeFormat().resolvedOptions();

	// The server returns `image` as a storage path (e.g. `pfp/{uid}/profile`).
	// It's stable per user, so the browser can cache it. When the photo changes
	// the profile query is invalidated, which bumps `dataUpdatedAt` and busts
	// the URL via the `?v=` query string.
	const pfp = query.data?.item?.image
		? `${apiUrl}files/${query.data.item.image}?v=${query.dataUpdatedAt}`
		: null;

	return {
		pfp,
		getPfpUrl,
		userId: userId!,
		tenant: tenant!,
		data: query.data,
		user: query.data?.item,
		operations: query.data?.operations || [],
		dateFormatOptions,

		defaultSidebarOpen: defaultSidebarOpen,

		isLoading: query.isLoading,
		isError: query.isError,
		isSuccess: query.isSuccess,
		error: query.error,
		refetch: query.refetch,
		isRefetching: query.isRefetching,
		isFetched: query.isFetched,
		isFetchedAfterMount: query.isFetchedAfterMount,
		isStale: query.isStale,
		isPlaceholderData: query.isPlaceholderData,
		isLoadingError: query.isLoadingError,
		isRefetchError: query.isRefetchError,
		isPaused: query.isPaused,
	};
}
